/**
 * Frontend Configuration Module
 * Centralized configuration for the Rithmo Zoom App frontend.
 */

declare global {
  interface Window {
    RITHMO_CONFIG?: {
      API_BASE_URL?: string;
      WS_BASE_URL?: string;
      DASHBOARD_TOKEN?: string;
      [key: string]: unknown;
    };
  }
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.RITHMO_CONFIG?.API_BASE_URL) {
    return window.RITHMO_CONFIG.API_BASE_URL;
  }
  try {
    if (import.meta.env?.VITE_API_BASE_URL) {
      return import.meta.env.VITE_API_BASE_URL;
    }
  } catch {
    // Fallback for non-Vite environments
  }
  return '';
};

export type WsBaseSource = 'explicit' | 'derived' | 'fallback';

let wsBaseSource: WsBaseSource = 'fallback';

const getWsBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.RITHMO_CONFIG?.WS_BASE_URL) {
    wsBaseSource = 'explicit';
    return window.RITHMO_CONFIG.WS_BASE_URL;
  }
  try {
    if (import.meta.env?.VITE_WS_BASE_URL) {
      wsBaseSource = 'explicit';
      return import.meta.env.VITE_WS_BASE_URL;
    }
  } catch {
    // Fallback for non-Vite environments
  }
  // Derive from API base URL so WebSocket targets same host as REST. The Zoom app
  // runs in a context where window.location may not be our backend (e.g. Zoom iframe),
  // so without this, ws would connect to the wrong host and "Host connected" never appears.
  const apiBase = getApiBaseUrl();
  const derived = deriveWsOriginFromApiBase(apiBase);
  if (derived) {
    wsBaseSource = 'derived';
    if (typeof console !== 'undefined' && console.info) {
      console.info('[Rithmo Config] WS base derived from API:', derived);
    }
    return derived;
  }
  if (typeof window !== 'undefined') {
    wsBaseSource = 'fallback';
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  return '';
};

/**
 * Derive WebSocket origin from API base URL (https -> wss, http -> ws, same host).
 * Used when WS_BASE_URL is not set so the Zoom app connects to the same backend for both REST and WS.
 */
export function deriveWsOriginFromApiBase(apiBase: string): string | null {
  if (!apiBase) return null;
  try {
    const url = new URL(apiBase);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.origin;
  } catch {
    return null;
  }
}

const getFeatureFlag = (key: string, defaultValue: boolean): boolean => {
  if (typeof window !== 'undefined' && window.RITHMO_CONFIG?.[key] !== undefined) {
    return Boolean(window.RITHMO_CONFIG[key]);
  }
  try {
    const envKey = `VITE_${key}`;
    if (import.meta.env?.[envKey] !== undefined) {
      return import.meta.env[envKey] === 'true';
    }
  } catch {
    // Fallback for non-Vite environments
  }
  return defaultValue;
};

function getDashboardToken(): string {
  if (typeof window !== 'undefined' && window.RITHMO_CONFIG?.DASHBOARD_TOKEN) {
    return window.RITHMO_CONFIG.DASHBOARD_TOKEN;
  }
  try {
    if (import.meta.env?.VITE_DASHBOARD_TOKEN) {
      return import.meta.env.VITE_DASHBOARD_TOKEN;
    }
  } catch {
    // Fallback for non-Vite environments
  }
  console.warn('[Config] VITE_DASHBOARD_TOKEN not configured - dashboard WebSocket may not work');
  return '';
}

const config = {
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
  },
  ws: {
    baseUrl: getWsBaseUrl(),
    source: wsBaseSource,
    reconnectInterval: 5000,
    maxReconnectAttempts: 10,
    heartbeatInterval: 30000,
  },
  endpoints: {
    meetingPresets: () => `/api/meeting/presets`,
    zoomState: (meetingId: string) => `/api/zoom/state/${encodeURIComponent(meetingId)}`,
    botStatus: (meetingUUID: string) => `/api/zoom/bot-status/${encodeURIComponent(meetingUUID)}`,
    createBot: '/api/bot/create',
    agendaInit: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/init`,
    agendaStatus: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/status`,
    agendaItems: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/items`,
    agendaStart: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/start`,
    agendaAdvance: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/advance`,
    agendaSkip: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/skip`,
    agendaDecision: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/decision`,
    agendaReset: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/reset`,
    agendaCommand: (meetingId: string) => `/api/agenda/${encodeURIComponent(meetingId)}/command`,
    interruptInit: (meetingId: string) => `/api/interrupt/${encodeURIComponent(meetingId)}/init`,
    interruptStatus: (meetingId: string) => `/api/interrupt/${encodeURIComponent(meetingId)}/status`,
    interruptToggle: (meetingId: string) => `/api/interrupt/${encodeURIComponent(meetingId)}/toggle`,
    interruptStrictness: (meetingId: string) => `/api/interrupt/${encodeURIComponent(meetingId)}/strictness`,
    interruptReset: (meetingId: string) => `/api/interrupt/${encodeURIComponent(meetingId)}/reset`,
    interruptResetParticipant: (meetingId: string, participantId: string) =>
      `/api/interrupt/${encodeURIComponent(meetingId)}/reset/${encodeURIComponent(participantId)}`,
    ttsIntro: '/api/tts/intro',
  },
  wsEndpoints: {
    moderation: (meetingUUID: string, hostParticipantUUID: string) =>
      `/ws/moderation?meetingUUID=${encodeURIComponent(meetingUUID)}&hostParticipantUUID=${encodeURIComponent(hostParticipantUUID)}`,
    dashboard: (token: string) => `/api/dashboard?token=${token}`,
  },
  features: {
    agendaEnforcerEnabled: getFeatureFlag('ENABLE_AGENDA_ENFORCER', true),
    interruptModelEnabled: getFeatureFlag('ENABLE_INTERRUPT_MODEL', true),
    ttsEnabled: getFeatureFlag('ENABLE_TTS', false),
    debugMode: getFeatureFlag('ENABLE_DEBUG_MODE', false),
    simulationMode: false,
  },
  ui: {
    quietThresholdMs: 120000,
    dominatingThreshold: 0.55,
    debugLogMaxEntries: 100,
    actionsFeedMaxEntries: 20,
    participantPollInterval: 10000,
    backendSyncInterval: 30000, // 30 seconds - keeps participant mapping fresh and enables auto-start when all join
    quietCheckInterval: 15000,
    botStatusPollInterval: 3000,
    // In Playwright share-capture mode poll every 500ms so state changes appear within one frame budget.
    // In normal Zoom App / dashboard mode poll every 2s to avoid unnecessary load.
    agendaStatusPollInterval: (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('shareCapture') === '1') ? 500 : 2000,
    interruptStatusPollInterval: 2000,
  },
  dashboardToken: getDashboardToken(),
};

export const buildApiUrl = (endpoint: string): string => `${config.api.baseUrl}${endpoint}`;
export const buildWsUrl = (endpoint: string): string => `${config.ws.baseUrl}${endpoint}`;
export default config;
